import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { DateRangePicker } from 'rsuite';
import { DateRange } from 'rsuite/esm/DateRangePicker';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Cancel } from '../../media/images/Cancel';
import { AnalystTubeCountDatatablestyle } from './utils/Constant';
import { predefinedRanges } from '../../Common/SearchHeader/utils/DatePickerRange';
import { getAnalystMasterStart } from '../Job/ScreenBidWorkSheetJob/redux/bidWorksheetSlice';
import { RootState } from '../../redux/rootState';
import { setLoader } from '../../redux/loaderSlice';
import { getAnalystTubeCountReport } from './redux/dashboardSlice';

interface IAnalystTubeCountModal {
  open: boolean;
  handleClose: () => void;
  handleReportDisplay: () => void;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 340,
    },
  },
};

const SELECT_ALL = { Text: 'Select All', Value: 'ALL' };

export default function AnalystTubeCountModal({
  open, handleClose, handleReportDisplay,
}: IAnalystTubeCountModal) {
  const dispatch = useDispatch();
  const [dateOpen, setDateOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const handleDateChange = (value: DateRange | null) => {
    setDateRange(value);
  };
  const { isLoading, analystList } = useSelector((state: RootState) => state.bidsReducer);
  const { userProfile } = useSelector((state: RootState) => state.userProfileReducer);
  const { isAnalystTubeRepLoading } = useSelector((state: RootState) => state.dashboardReducer);

  const names = useMemo(() => [
    SELECT_ALL,
    ...analystList,
  ], [analystList]);

  useEffect(() => {
    dispatch(getAnalystMasterStart());
  }, []);

  useEffect(() => {
    if (isLoading || isAnalystTubeRepLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [isLoading, isAnalystTubeRepLoading]);

  const [personName, setPersonName] = useState<typeof names>([]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    // Ensure value is string[] (it always will be in multiple mode)
    const selectedValues = typeof value === 'string' ? [value] : value;

    // Map strings back to objects
    const selectedObjects = selectedValues.map((v) => {
      const obj = names.find((n) => n.Value === v);
      if (!obj) throw new Error(`Value ${v} not found in options`);
      return obj;
    });

    if (selectedObjects.some((s) => s.Value === SELECT_ALL.Value)) {
      if (personName.some((p) => p.Value === SELECT_ALL.Value)) {
        setPersonName(selectedObjects.filter((item) => item !== SELECT_ALL));
      } else {
        setPersonName(names);
      }
    } else if ((selectedObjects.length === names.length - 1) && (personName?.length === names.length)) {
      setPersonName([]);
    } else {
      setPersonName(selectedObjects);
    }
  };

  const closeModal = () => {
    setDateRange(null);
    setPersonName([]);
    handleClose();
  };
  const successCallback = () => {
    handleReportDisplay();
    closeModal();
  };

  const isButtonEnabled = useMemo(() => dateRange && personName?.length > 0, [dateRange, personName]);

  const handleReport = () => {
    if (isButtonEnabled) {
      const StartDate = dayjs(dateRange?.[0])?.format('DD-MM-YYYY');
      const EndDate = dayjs(dateRange?.[1])?.format('DD-MM-YYYY');
      const AnalystIds = personName?.filter((person) => person.Value !== SELECT_ALL.Value)?.map((item) => item?.Value);
      const Email = userProfile?.Email;

      const payload = {
        StartDate, EndDate, AnalystIds, Email,
      };
      dispatch(getAnalystTubeCountReport({ pay: payload, callback: successCallback }));
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={AnalystTubeCountDatatablestyle}>
        <div className="customerInfo_container flex-row">
          <div className="customerInfo">
            Analyst Tube Count
          </div>
          <div className="cancel-icon" onClick={() => closeModal()}>
            <Cancel />
          </div>

        </div>
        <div className="analystTubeCountModalHeaderRowAlign">
          <div className="dateRangeFilterRangeDatePicker">
            <p className="analystModalLabel">Select Date Range</p>
            <DateRangePicker
              name="Range"
              open={dateOpen}
              onClick={() => setDateOpen(!dateOpen)}
              onOk={() => setDateOpen(false)}
              value={dateRange}
              onChange={handleDateChange}
              ranges={predefinedRanges}
              placeholder="Date Range"
              className="dateRangeFilterCustomDateRangeCalender"
              character=" - "
              showCleanButton
              format="MM-dd-yyyy"
              placement="bottomStart"
            />
          </div>
          <FormControl sx={{
            width: 340,
            marginTop: '-20px',
          }}
          >
            <p className="analystModalLabel">Select Analysts</p>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple
              displayEmpty
              sx={{ height: 36, fontSize: '14px' }}
              value={personName.map((p) => p.Value)}
              onChange={handleChange}
              input={<OutlinedInput placeholder="Analyst" />}
              MenuProps={MenuProps}
              // renderValue={(selected) => (selected.length === 0 ? (
              //   <span style={{ color: '#a09f9fff' }}>Analyst</span>
              // ) : (
              //   selected?.filter((item) => item !== SELECT_ALL)?.join(', ')
              // ))}
              renderValue={(selected) => {
                const selectedObjects = names.filter((n) => selected.includes(n.Value) && n.Value !== SELECT_ALL.Value);
                return selectedObjects.length === 0
                  ? <span style={{ color: '#a09f9fff' }}>Analyst</span>
                  : selectedObjects.map((s) => s.Text).join(', ');
              }}
              inputProps={{ 'aria-label': 'Without label' }}
            >
              {names.map((name) => (
                <MenuItem
                  key={name.Value}
                  value={name.Value}
                  sx={{
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    paddingLeft: 0,
                    '&:hover': {
                      backgroundColor: '#f0f9f4', // light green hover
                    },

                    '&.Mui-selected': {
                      backgroundColor: 'rgb(229, 246, 237)', // when selected
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#c0e8c0', // hover on selected
                      },
                    },
                  }}
                >
                  <Checkbox
                    checked={personName.some((p) => p.Value === name.Value)}
                    sx={{
                      color: '#9e9e9e',
                      '&.Mui-checked': {
                        color: '#00a751', // green when checked
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 20, // checkbox size
                      },
                    }}
                  />
                  <ListItemText
                    primary={name.Text}
                    primaryTypographyProps={{ fontSize: '14px' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            className={isButtonEnabled ? 'button_save_and_next' : 'button_cancel cursorNotAllowed'}
            sx={{ height: 36, marginTop: '20px' }}
            onClick={handleReport}
          >
            Get Tube Count
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
